/** @file frameList: one frame of the runtime contract, evaluate -> plants -> classify -> cells -> place. */
import type { FacetPick } from '@zilo/pose3d-facet/contract';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { ClipState, PoseBuffer, PoseParams } from '@zilo/pose3d-pose/contract';
import type { Plants } from '@zilo/pose3d-pose/events/eventTypes';
import type { Camera, ImpostorList } from '@zilo/pose3d-render-core/contract';
import { applyBundleCells } from '@zilo/pose3d-runtime-babylon/bundle/applyBundleCells';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import type { FrameScratch } from '@zilo/pose3d-runtime-babylon/instance/frameScratch';
import type { RuntimeFns } from '@zilo/pose3d-runtime-babylon/instance/runtimeFns';

/**
 * Host steps inside a frame: `afterEvaluate` sees the evaluated pose (events, plant updates) and returns the plants to
 * apply; `afterPlace` may append entries (effects) after `count` and returns the new count.
 */
export type FrameHooks = {
  afterEvaluate?: (pose: PoseBuffer) => Plants | null;
  afterPlace?: (list: ImpostorList, count: number, pose: PoseBuffer) => number;
};

/** Everything one frame reads; `puppet` is `toRuntimePuppet(bundle.puppet)`. */
export type RuntimeFrame = {
  bundle: LoadedBundle; puppet: Puppet; state: ClipState | null; params: PoseParams; camera: Camera; plants: Plants | null;
  entityPos: Vec3; hooks: FrameHooks;
};

/** The frame's pose, picks and list (`count` entries), all in `scratch`. */
export type FrameResult = { pose: PoseBuffer; picks: FacetPick[]; list: ImpostorList; count: number };

/**
 * `evaluate` -> `afterEvaluate` -> `applyPlants` (when there are plants; pose decides `ikMode`) -> `classify` with the
 * camera's real yaw and pitch -> `applyBundleCells` -> `place(..., entityPos, ..., params)` (the host's partTransform
 * and shakeClock, 22.3) -> `afterPlace`. Pure given its inputs (the hooks aside).
 */
export function frameList(frame: RuntimeFrame, fns: RuntimeFns, scratch: FrameScratch): FrameResult {
  const { puppet, params, camera } = frame;
  fns.evaluate(puppet, frame.state, params, scratch.pose);
  const plants = frame.hooks.afterEvaluate?.(scratch.pose) ?? frame.plants;
  if (plants !== null && plants.size > 0) fns.applyPlants(puppet, plants, frame.entityPos, params, scratch.pose);
  fns.classify(puppet, scratch.pose, { yawDeg: camera.yawDeg, pitchDeg: camera.pitchDeg }, params, scratch.picks);
  applyBundleCells(frame.bundle.puppet, scratch.picks);
  let count = fns.place(puppet, scratch.pose, scratch.picks, camera, frame.bundle.profile, frame.entityPos, scratch.list, params);
  if (frame.hooks.afterPlace !== undefined) count = frame.hooks.afterPlace(scratch.list, count, scratch.pose);
  return { pose: scratch.pose, picks: scratch.picks, list: scratch.list, count };
}
